// Initialize Firebase
import { getToken, onMessage } from "firebase/messaging";
import { useEffect } from "react";
import { messaging } from "../hooks/useAuth";
import { postNotificationPushMemberRequest } from "../utils/api/postPushNotificationPushMember";
import { getDeviceId } from "../components/organisms/PushNotificationModal";

export const NotificationStatus = Object.freeze({
  GRANTED: 'granted',
  DENIED: 'denied',
  DEFAULT: 'default'
});

// FCMトークンをLocalStorageに保存するキー
export const LIB_FCM_TOKEN_KEY = 'LIB_FCM_TOKEN';

export const fetchToken = async () => {
  try {
    const fcmMessaging = await messaging();
    let token;
    const registration = await navigator.serviceWorker.ready;
    if (fcmMessaging) {
      const MAX_RETRIES = 10;
      // retry til getting the token
      // reference: https://github.com/firebase/firebase-js-sdk/issues/7575
      for (let i = 0; i < MAX_RETRIES; i++) {
        try {
          token = await getToken(fcmMessaging, {
            vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
            serviceWorkerRegistration: registration
          });
        } catch(e) {
          console.log(e);
        }
        if (token) {
          return token;
        }
      }
    }
    return null;
  } catch (err) {
    console.error('Fatal error in fetchToken:', err);
    return null;
  }
};

export const getNotificationPermissionAndToken = async (
  skipPermissionRequest?: boolean
) => {
  const fcmMessaging = await messaging();
  if (!fcmMessaging) {
    return null;
  }
  if (!("Notification" in window)) {
    return null;
  }
  if (Notification.permission === NotificationStatus.GRANTED) {
    return await fetchToken();
  }

  if (
    !skipPermissionRequest &&
    Notification.permission !== NotificationStatus.DENIED
  ) {
    const permission = await Notification.requestPermission();
    if (permission === NotificationStatus.GRANTED) {
      return await fetchToken();
    }
  }
  return null;
};

/**
 * FCMトークンをサーバーと同期する
 * トークンが変更されている場合のみAPIを呼び出す
 */
export const syncFCMToken = async (
  contractCompanyId: number,
  email: string
): Promise<boolean> => {
  try {
    // 通知が許可されていない場合は同期不要
    if (Notification.permission !== NotificationStatus.GRANTED) {
      return false;
    }

    const currentToken = await fetchToken();
    if (!currentToken) {
      return false;
    }

    // LocalStorageの保存済みトークンと比較
    const storedToken = localStorage.getItem(LIB_FCM_TOKEN_KEY);
    if (storedToken === currentToken) {
      // トークンに変更なし、同期不要
      return true;
    }

    // 新トークンをサーバーに送信
    const deviceId = getDeviceId();
    const result = await postNotificationPushMemberRequest(
      deviceId,
      currentToken,
      contractCompanyId,
      email,
      1 // allow_push_notification: 許可状態
    );

    if (result.isSuccess) {
      // 同期成功時にLocalStorageを更新
      localStorage.setItem(LIB_FCM_TOKEN_KEY, currentToken);
      console.log('FCMトークンを同期しました');
    }

    return result.isSuccess;
  } catch (error) {
    console.error('FCMトークン同期エラー:', error);
    return false;
  }
};

export const useFireBaseCloudMessage = (loginUser: any) => {
  useEffect(() => {
    if (!loginUser) return;
    const setupListener = async () => {
      const message = await messaging();
      if (!message) return;

      const unsubscribe = onMessage(message, (payload) => {
        if (Notification.permission !== NotificationStatus.GRANTED) return;
        const title = payload?.data?.title ?? "";
        const notification = {
          body: payload?.data?.body,
          data: { link: payload?.data?.link },
        };
        navigator.serviceWorker.ready.then(function (registration) {
          setTimeout(() => {
            registration?.showNotification(title, notification);
          }, 100);
        });
      });
      return unsubscribe;
    };
    let unsubscribe: any = null;
    setupListener();
    return () => unsubscribe?.();
  }, [JSON.stringify(loginUser)]);

  return null;
};
